/**
 * Geolocation Thunks
 *
 * Awaitable setup (storage restore + permission query) lives here so callers
 * like `initExplore` can block on it. Runtime side effects stay in
 * `geolocation-middleware.ts`.
 */

import { MAX_PAUSED_AGE_MS, MAX_RESTORE_AGE_MS } from "@/constants/geolocation"
import type { UserPosition } from "@/types/geolocation"

import { nowMillis } from "@/lib/date-utils"
import { isClientSide } from "@/lib/is-client-side"
import { webStorage } from "@/lib/web-storage"
import { geoStopRequested } from "./geolocation-actions"
import { geolocationSlice } from "./geolocation-slice"
import type { AppDispatch, RootState } from "./redux-store"

// Memoized so concurrent dispatches share one init. Reset by a `fullReset`
// listener in `geolocation-middleware.ts` so logout/sign-in re-runs init.
let initPromise: Promise<void> | null = null

export function resetInitGeoMemo() {
  initPromise = null
}

export function initGeo() {
  return (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    if (!isClientSide()) return Promise.resolve()
    if (initPromise) return initPromise

    initPromise = (async () => {
      const stored = webStorage.tryGetJSON<UserPosition>(
        "mindtrip.userPosition",
        { type: "session" }
      )

      let validPosition: UserPosition | null = null
      if (stored && nowMillis() - stored.timestamp < MAX_RESTORE_AGE_MS) {
        validPosition = stored
      } else if (stored) {
        webStorage.removeItem("mindtrip.userPosition", { type: "session" })
      }

      dispatch(
        geolocationSlice.actions.storageRestored({ position: validPosition })
      )

      // Re-check freshness on init: mobile Safari throttles timers when backgrounded.
      const stateAfterRestore = getState().geolocation
      if (
        stateAfterRestore.status === "paused" &&
        stateAfterRestore.pausedAt &&
        nowMillis() - stateAfterRestore.pausedAt > MAX_PAUSED_AGE_MS
      ) {
        dispatch(geolocationSlice.actions.pausedDegraded())
      }
      if (
        stateAfterRestore.status === "restored" &&
        stateAfterRestore.position &&
        nowMillis() - stateAfterRestore.position.timestamp > MAX_RESTORE_AGE_MS
      ) {
        webStorage.removeItem("mindtrip.userPosition", { type: "session" })
        dispatch(geolocationSlice.actions.restoredExpired())
      }

      if ("permissions" in navigator) {
        try {
          const result = await navigator.permissions.query({
            name: "geolocation",
          })
          // PermissionState is a subset of GeoPermission; direct assignment ok.
          dispatch(geolocationSlice.actions.permissionUpdated(result.state))

          result.onchange = () => {
            dispatch(geolocationSlice.actions.permissionUpdated(result.state))
            // Permission left "granted" (revoked or reset): drop cached fix.
            if (result.state !== "granted") {
              dispatch(geoStopRequested("user"))
            }
          }
        } catch {
          // Treat query failures as "prompt" so awaiters don't stall on "unknown".
          dispatch(geolocationSlice.actions.permissionUpdated("prompt"))
        }
      } else {
        // No Permissions API: same fallback to keep awaiters unblocked.
        dispatch(geolocationSlice.actions.permissionUpdated("prompt"))
      }

      // Cross-session revoke: storage held a position but permission is no
      // longer granted. Drop it so we don't render a stale marker.
      const finalState = getState().geolocation
      if (finalState.position && finalState.permission !== "granted") {
        dispatch(geoStopRequested("user"))
      }
    })()

    return initPromise
  }
}

// For tests only.
export function __resetInitGeoForTesting() {
  resetInitGeoMemo()
}
